'use client'

import React, { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Progress } from '../ui/progress'
import { Alert, AlertDescription } from '../ui/alert'
import { Separator } from '../ui/separator'
import { Label } from '../ui/label'
import {
  TrendingUp,
  TrendingDown,
  Shield,
  AlertTriangle,
  Plus,
  X,
  Download,
  Wand2,
  AlertCircle,
  CheckCircle,
  Lightbulb,
  Target,
  Zap,
  Brain
} from 'lucide-react'
import { Brand } from '../../utils/mockData'

interface SWOTAnalysisProps {
  brand: Brand
}

interface SWOTItem {
  id: string
  text: string
  description: string
  priority: 'high' | 'medium' | 'low'
  category?: string
}

interface SWOTData {
  strengths: SWOTItem[]
  weaknesses: SWOTItem[]
  opportunities: SWOTItem[]
  threats: SWOTItem[]
}

interface StrategicInitiative {
  id: string
  title: string
  description: string
  type: 'SO' | 'WO' | 'ST' | 'WT'
  relatedItems: string[]
  priority: 'high' | 'medium' | 'low'
  timeline: string
}

const SWOT_COLORS = {
  strengths: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: 'text-green-600',
    header: 'text-green-800'
  },
  weaknesses: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-600',
    header: 'text-red-800'
  },
  opportunities: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-600',
    header: 'text-blue-800'
  },
  threats: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    icon: 'text-orange-600',
    header: 'text-orange-800'
  }
}

const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-green-100 text-green-700'
}

const STRATEGY_TYPES = {
  'SO': { name: 'Strength-Opportunity', description: 'Use strengths to capitalize on opportunities', color: 'bg-emerald-100 text-emerald-700' },
  'WO': { name: 'Weakness-Opportunity', description: 'Overcome weaknesses to pursue opportunities', color: 'bg-blue-100 text-blue-700' },
  'ST': { name: 'Strength-Threat', description: 'Use strengths to avoid threats', color: 'bg-purple-100 text-purple-700' },
  'WT': { name: 'Weakness-Threat', description: 'Minimize weaknesses and avoid threats', color: 'bg-gray-100 text-gray-700' }
}

export function SWOTAnalysis({ brand }: SWOTAnalysisProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [activeView, setActiveView] = useState<'analysis' | 'strategies'>('analysis')

  const [swotData, setSWOTData] = useState<SWOTData>({
    strengths: [],
    weaknesses: [],
    opportunities: [],
    threats: []
  })

  const [strategies, setStrategies] = useState<StrategicInitiative[]>([])

  useEffect(() => {
    loadSWOTData()
  }, [brand.id])

  const loadSWOTData = () => {
    try {
      const savedData = localStorage.getItem(`swotAnalysis_${brand.id}`)
      if (savedData) {
        const data = JSON.parse(savedData)
        setSWOTData(data.swotData || {
          strengths: [],
          weaknesses: [],
          opportunities: [],
          threats: []
        })
        setStrategies(data.strategies || [])
      }
    } catch (error) {
      console.error('Failed to load SWOT data:', error)
    }
  }

  const saveSWOTData = async () => {
    setIsLoading(true)
    try {
      const data = {
        swotData,
        strategies,
        lastUpdated: new Date().toISOString()
      }
      
      localStorage.setItem(`swotAnalysis_${brand.id}`, JSON.stringify(data))
      setHasUnsavedChanges(false)
      
      // Show success feedback
      setTimeout(() => setIsLoading(false), 500)
    } catch (error) {
      console.error('Failed to save SWOT data:', error)
      setIsLoading(false)
    }
  }

  const addSWOTItem = (category: keyof SWOTData) => {
    const newItem: SWOTItem = {
      id: Date.now().toString(),
      text: '',
      description: '',
      priority: 'medium'
    }
    
    setSWOTData(prev => ({
      ...prev,
      [category]: [...prev[category], newItem]
    }))
    setHasUnsavedChanges(true)
  }

  const updateSWOTItem = (category: keyof SWOTData, id: string, updates: Partial<SWOTItem>) => {
    setSWOTData(prev => ({
      ...prev,
      [category]: prev[category].map(item => 
        item.id === id ? { ...item, ...updates } : item
      )
    }))
    setHasUnsavedChanges(true)
  }

  const removeSWOTItem = (category: keyof SWOTData, id: string) => {
    setSWOTData(prev => ({
      ...prev,
      [category]: prev[category].filter(item => item.id !== id)
    }))
    setHasUnsavedChanges(true)
  }

  const generateAISuggestions = async (category: keyof SWOTData) => {
    setIsLoading(true)
    try {
      // Simulate AI generation
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      const suggestions = {
        strengths: [
          { text: 'Strong brand recognition', description: 'Well-established brand identity in the market' },
          { text: 'Innovative product features', description: 'Unique capabilities that differentiate from competitors' },
          { text: 'Experienced team', description: 'Skilled professionals with industry expertise' }
        ],
        weaknesses: [
          { text: 'Limited market presence', description: 'Smaller reach compared to major competitors' },
          { text: 'Resource constraints', description: 'Limited budget for marketing and expansion' },
          { text: 'New brand recognition', description: 'Still building awareness in the target market' }
        ],
        opportunities: [
          { text: 'Growing market demand', description: 'Increasing interest in the product category' },
          { text: 'Digital transformation trends', description: 'Shift towards online solutions creates opportunities' },
          { text: 'Partnership potential', description: 'Opportunities for strategic collaborations' }
        ],
        threats: [
          { text: 'Intense competition', description: 'Many established players in the market' },
          { text: 'Economic uncertainty', description: 'Market conditions may affect customer spending' },
          { text: 'Technology disruption', description: 'Rapid changes may make current solutions obsolete' }
        ]
      }
      
      const categorySuggestions = suggestions[category] || []
      const newItems = categorySuggestions.map((suggestion, index) => ({
        id: `ai-${Date.now()}-${index}`,
        text: suggestion.text,
        description: suggestion.description,
        priority: 'medium' as const
      }))
      
      setSWOTData(prev => ({
        ...prev,
        [category]: [...prev[category], ...newItems]
      }))
      setHasUnsavedChanges(true)
    } catch (error) {
      console.error('Failed to generate AI suggestions:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const addStrategy = () => {
    const newStrategy: StrategicInitiative = {
      id: Date.now().toString(),
      title: '',
      description: '',
      type: 'SO',
      relatedItems: [],
      priority: 'medium',
      timeline: ''
    }
    setStrategies([...strategies, newStrategy])
    setHasUnsavedChanges(true)
  }

  const updateStrategy = (id: string, updates: Partial<StrategicInitiative>) => {
    setStrategies(strategies.map(strategy => 
      strategy.id === id ? { ...strategy, ...updates } : strategy
    ))
    setHasUnsavedChanges(true)
  }

  const removeStrategy = (id: string) => {
    setStrategies(strategies.filter(strategy => strategy.id !== id))
    setHasUnsavedChanges(true)
  }

  const exportSWOT = () => {
    const data = {
      brand: brand.name,
      swotAnalysis: swotData,
      strategicInitiatives: strategies,
      exportedAt: new Date().toISOString()
    }
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${brand.name}-swot-analysis.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const calculateCompletionProgress = () => {
    const totalItems = Object.values(swotData).flat().length
    const filledItems = Object.values(swotData).flat().filter(item => item.text.trim() !== '').length
    const strategiesCount = strategies.length
    
    if (totalItems === 0 && strategiesCount === 0) return 0
    
    const swotProgress = totalItems > 0 ? (filledItems / totalItems) * 80 : 0
    const strategyProgress = strategiesCount > 0 ? 20 : 0
    
    return Math.round(swotProgress + strategyProgress)
  }

  const progress = calculateCompletionProgress()

  const renderSWOTSection = (
    category: keyof SWOTData, 
    title: string, 
    icon: React.ReactNode,
    description: string
  ) => {
    const items = swotData[category]
    const colors = SWOT_COLORS[category]
    
    return (
      <Card className={`${colors.bg} ${colors.border}`}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`${colors.icon}`}>
                {icon}
              </div>
              <div>
                <CardTitle className={`${colors.header}`}>{title}</CardTitle>
                <CardDescription className="text-sm">{description}</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => generateAISuggestions(category)}
                disabled={isLoading}
                className="bg-white/50 hover:bg-white/80"
              >
                <Wand2 className="w-3 h-3 mr-1" />
                AI
              </Button>
              <Button
                size="sm"
                onClick={() => addSWOTItem(category)}
                className="bg-white/50 hover:bg-white/80"
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {items.length === 0 ? (
            <div className="text-center py-6 text-slate-500">
              <p className="text-sm">No {category} identified yet</p>
              <p className="text-xs">Click the + button or try AI suggestions</p>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="bg-white/60 rounded-lg p-3 border border-white/40">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder={`${title.slice(0, -1)} item...`}
                        value={item.text}
                        onChange={(e) => updateSWOTItem(category, item.id, { text: e.target.value })}
                        className="text-sm border-0 bg-white/70 focus:bg-white"
                      />
                      <Badge 
                        className={`cursor-pointer text-xs ${PRIORITY_COLORS[item.priority]}`}
                        onClick={() => {
                          const priorities: ('high' | 'medium' | 'low')[] = ['low', 'medium', 'high']
                          const currentIndex = priorities.indexOf(item.priority)
                          const nextPriority = priorities[(currentIndex + 1) % priorities.length]
                          updateSWOTItem(category, item.id, { priority: nextPriority })
                        }}
                      >
                        {item.priority}
                      </Badge>
                    </div>
                    <Textarea
                      placeholder="Description or details..."
                      value={item.description}
                      onChange={(e) => updateSWOTItem(category, item.id, { description: e.target.value })}
                      rows={2}
                      className="text-xs border-0 bg-white/70 focus:bg-white resize-none"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSWOTItem(category, item.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-500 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">SWOT Analysis Board</h1>
              <p className="text-slate-600">Analyze strengths, weaknesses, opportunities, and threats</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center bg-white rounded-lg border border-slate-200 p-1">
              <Button
                size="sm"
                variant={activeView === 'analysis' ? 'default' : 'ghost'}
                onClick={() => setActiveView('analysis')}
                className="text-xs"
              >
                <Shield className="w-3 h-3 mr-1" />
                Analysis
              </Button>
              <Button
                size="sm"
                variant={activeView === 'strategies' ? 'default' : 'ghost'}
                onClick={() => setActiveView('strategies')}
                className="text-xs"
              >
                <Target className="w-3 h-3 mr-1" />
                Strategies
              </Button>
            </div>
            <Button variant="outline" onClick={exportSWOT}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button 
              onClick={saveSWOTData}
              disabled={isLoading}
              className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-green-800">Analysis Completion</span>
              <span className="text-sm text-green-600">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2 bg-green-100" />
          </CardContent>
        </Card>

        {hasUnsavedChanges && (
          <Alert className="mt-4 border-yellow-200 bg-yellow-50">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              You have unsaved changes. Don't forget to save your progress.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Content */}
      {activeView === 'analysis' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {renderSWOTSection(
            'strengths',
            'Strengths',
            <TrendingUp className="w-5 h-5" />,
            'Internal positive factors'
          )}
          
          {renderSWOTSection(
            'weaknesses',
            'Weaknesses',
            <TrendingDown className="w-5 h-5" />,
            'Internal areas for improvement'
          )}
          
          {renderSWOTSection(
            'opportunities',
            'Opportunities',
            <Lightbulb className="w-5 h-5" />,
            'External positive possibilities'
          )}
          
          {renderSWOTSection(
            'threats',
            'Threats',
            <AlertTriangle className="w-5 h-5" />,
            'External challenges or risks'
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Strategic Initiatives
                  </CardTitle>
                  <CardDescription>
                    Develop strategies based on your SWOT analysis
                  </CardDescription>
                </div>
                <Button onClick={addStrategy} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Strategy
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {strategies.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Target className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No strategic initiatives defined yet</p>
                  <p className="text-sm">Create strategies based on your SWOT analysis</p>
                </div>
              ) : (
                strategies.map((strategy) => (
                  <Card key={strategy.id} className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3 flex-1">
                          <Input
                            placeholder="Strategy title..."
                            value={strategy.title}
                            onChange={(e) => updateStrategy(strategy.id, { title: e.target.value })}
                            className="font-medium"
                          />
                          <Badge className={STRATEGY_TYPES[strategy.type].color}>
                            {STRATEGY_TYPES[strategy.type].name}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeStrategy(strategy.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Description</Label>
                          <Textarea
                            placeholder="Strategy description..."
                            value={strategy.description}
                            onChange={(e) => updateStrategy(strategy.id, { description: e.target.value })}
                            rows={3}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Timeline</Label>
                          <Input
                            placeholder="Implementation timeline..."
                            value={strategy.timeline}
                            onChange={(e) => updateStrategy(strategy.id, { timeline: e.target.value })}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}